import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import { connectToHubUser } from "src/redux/slices/hub";
import { returnToEmployeeSelection } from "src/utils/employeeSessionKeepAlive";
import { forceSelectionNotice } from "src/utils/employeeSessionEvents";
import { getActiveDevices } from "src/redux/slices/devices";
import { getSessionData } from "src/redux/slices/authJwt";

// F-device-enforcement · 2026-08-24
// Hook global que escucha el evento `device-enforcement/{restauranteId}`
// del UserHub. El backend lo emite cuando el admin (desde el panel de
// Dispositivos) asigna un empleado a un device o activa/desactiva
// "requerir selección de empleado".
//
// Cada TPV compara el `sessionIdentifier` del payload con el suyo local
// (localStorage["sessionIdentifier"]) y si coincide aplica:
//   • `assignedEmployeeId` seteado → guardamos el valor en
//     localStorage["assignedEmployeeId"] + refresh de la página (o
//     futuro: hot-swap del empleado sin recargar). Recargar es la vía
//     más simple: fuerza el nuevo pin/context sin duplicar lógica de
//     KioskOverlay.
//   • `requireEmployeeSelection` cambia → guardamos flag; el kiosko lo
//     consulta al montar para decidir si abrir modal bloqueante.
//
// Notas:
//   • Sin `restauranteId` (login screen, etc.) el hook no se conecta.
//   • Dedup por timestamp — el server manda el mismo evento tras
//     reconexión SignalR ocasional.
export default function useDeviceEnforcementHub() {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.authJwt);
  const seenRef = useRef(new Set());

  const restauranteId = user?.id;

  useEffect(() => {
    if (!restauranteId) return undefined;

    const handler = (payload) => {
      if (!payload?.sessionIdentifier) return;
      const sig = `${payload.sessionIdentifier}:${payload.assignedEmployeeId}:${payload.requireEmployeeSelection}:${payload.timestamp}`;
      if (seenRef.current.has(sig)) return;
      seenRef.current.add(sig);
      if (seenRef.current.size > 100) {
        const first = seenRef.current.values().next().value;
        seenRef.current.delete(first);
      }

      let localSid = null;
      try { localSid = localStorage.getItem("sessionIdentifier"); } catch { /* ignore */ }
      if (!localSid || localSid !== payload.sessionIdentifier) return;

      // Persistir estado — el KioskOverlay y demás componentes que
      // gestionan sesión de empleado consultan estos flags al montar.
      try {
        if (payload.assignedEmployeeId) {
          localStorage.setItem("assignedEmployeeId", String(payload.assignedEmployeeId));
          if (payload.assignedEmployeeName) {
            localStorage.setItem("assignedEmployeeName", payload.assignedEmployeeName);
          }
        } else {
          localStorage.removeItem("assignedEmployeeId");
          localStorage.removeItem("assignedEmployeeName");
        }
        localStorage.setItem(
          "deviceRequireEmployeeSelection",
          payload.requireEmployeeSelection ? "true" : "false",
        );
      } catch { /* private mode */ }

      // Hot-reload — la manera menos frágil de aplicar los nuevos
      // permisos + reejecutar los guards de auth con el empleado
      // correcto sin duplicar el flujo de KioskOverlay. Fase 2: hacer
      // hot-swap real sin recargar.
      try { window.location.reload(); } catch { /* ignore */ }
    };

    // 2026-08-24 · Handler para el evento one-shot `device-force-selection`.
    // A diferencia de `device-enforcement`, éste NO persiste flags en
    // localStorage ni recarga la página — solo dispara `openKiosk` en
    // caliente si el sessionIdentifier coincide con el nuestro. Permite
    // al admin re-forzar la selección de empleado tantas veces como
    // haga falta sin desincronizarse por reloads.
    const forceHandler = (payload) => {
      if (!payload?.sessionIdentifier) return;
      const sig = `force:${payload.sessionIdentifier}:${payload.timestamp}`;
      if (seenRef.current.has(sig)) return;
      seenRef.current.add(sig);
      if (seenRef.current.size > 100) {
        const first = seenRef.current.values().next().value;
        seenRef.current.delete(first);
      }
      let localSid = null;
      try { localSid = localStorage.getItem("sessionIdentifier"); } catch { /* ignore */ }
      if (!localSid || localSid !== payload.sessionIdentifier) return;

      // 2026-10-06 · Al momento, sin esperar a la siguiente petición: se
      // suelta la sesión del empleado (el servidor ya la revocó; seguir
      // mandándola solo daría 401) y el kiosko enseña el motivo como aviso
      // («Carlos ya está siendo utilizado en otro dispositivo…»).
      dispatch(returnToEmployeeSelection(forceSelectionNotice(payload)));
    };

    // 2026-09-09 · Handler para `device-active-employee-changed/{id}`.
    // Emitido por el backend tras un login PIN que actualiza
    // `Devices.EmployeeId`. Este hook lo escuchan TODOS los clientes del
    // restaurante — el TPV afectado no necesita hacer nada (ya sabe qué
    // empleado tiene activo), pero el ADMIN sí: la vista Usuario >
    // Sesiones y el DevicesEnforcementPanel muestran esa columna y la
    // querían al día sin F5. Refetch de los dos slices que pintan la
    // columna "Empleado activo".
    const activeChangedHandler = (payload) => {
      if (!payload?.deviceId) return;
      const sig = `active:${payload.deviceId}:${payload.activeEmployeeId}:${payload.timestamp}`;
      if (seenRef.current.has(sig)) return;
      seenRef.current.add(sig);
      if (seenRef.current.size > 100) {
        const first = seenRef.current.values().next().value;
        seenRef.current.delete(first);
      }
      dispatch(getActiveDevices());
      dispatch(getSessionData());
    };

    const cleanup = CreateConnection(
      buildHubUrl("/hub/userhub", { restauranteId }),
      [
        { id: `device-enforcement/${restauranteId}`, operator: handler },
        { id: `device-force-selection/${restauranteId}`, operator: forceHandler },
        { id: `device-active-employee-changed/${restauranteId}`, operator: activeChangedHandler },
      ],
      dispatch,
      connectToHubUser,
      "user",
    );

    return cleanup;
  }, [restauranteId, dispatch]);
}
